// Dip sites layer family.
//
// Our org's recorded water sources (helicopter dip sites). Single
// GeoJSON source fed from useDipSiteStore. Two visual layers:
//   - dip-sites-circle: colored circle by ownership_class (federal
//     green / private amber / state blue / tribal purple / unknown
//     gray). White stroke so the dot reads on any base layer.
//   - dip-sites-label: source name above the dot at z>=10.
//
// NOTE: id suffix '-mount' to avoid colliding with DipSitesController's
// existing registerStyleRebuild entry (id 'dipSites'). The controller
// owns apply behavior — visibility AND data re-push after a basemap
// rebuild, gated on store subscriptions. If this module registered
// under 'dipSites', it would silently replace the controller's restore
// in the registry Map.
//
// With a separate id, both restores run on basemap rebuild: this module
// re-mounts the sources/layers; the controller's 'dipSites' entry then
// re-runs visibility + data re-push. Ordering between two registry entries
// isn't guaranteed, but the controller's apply() is guarded by
// map.getLayer('dip-sites-circle') checks, so a no-op-then-mount-then-apply
// sequence is safe.
//
// See src/map/layers/terrain.ts for the canonical version of this pattern.
// Extracted from addOverlays.ts as HI-1 batch 7 round 4 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids, minzoom,
// and layer order all byte-identical to the pre-extraction form.
// DIPSITE_OWNERSHIP_COLOR_EXPR moved here from addOverlays.ts (was only
// used in this block).

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { DIPSITE_OWNERSHIP_COLOR_EXPR } from '../dipSitesLayer';

/**
 * Mount the dip sites source and layers.
 * Idempotent on the source.
 */
export function addDipSitesLayers(map: MlMap): void {
  if (!map.getSource('dip-sites')) {
    map.addSource('dip-sites', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'dip-sites-circle',
      type: 'circle',
      source: 'dip-sites',
      layout: { visibility: 'none' },
      paint: {
        'circle-color': DIPSITE_OWNERSHIP_COLOR_EXPR as any,
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          5, 4,
          8, 6,
          12, 9,
        ],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.4,
        'circle-opacity': 0.95,
      },
    });
    map.addLayer({
      id: 'dip-sites-label',
      type: 'symbol',
      source: 'dip-sites',
      minzoom: 10,
      layout: {
        visibility: 'none',
        'text-field': ['get', 'name'],
        'text-size': [
          'interpolate', ['linear'], ['zoom'],
          10, 10, 13, 12,
        ],
        'text-anchor': 'top',
        'text-offset': [0, 0.8],
        'text-allow-overlap': false,
        'text-padding': 4,
        'symbol-placement': 'point',
      },
      paint: {
        'text-color': '#e5e7eb',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.3,
      },
    });
  }
}

// NOTE: id suffix '-mount' to avoid colliding with DipSitesController's
// existing registerStyleRebuild entry (id 'dipSites'). See comment at
// top of file and src/map/layers/terrain.ts for full explanation.
registerLayerFamily({
  id: 'dipSites-mount',
  label: 'Dip sites mount',
  sourceId: 'dip-sites',
  layerIds: ['dip-sites-circle', 'dip-sites-label'],
  mount: (map) => addDipSitesLayers(map),
  // NO applyState — DipSitesController owns apply behavior.
});
